import BaseModel from './BaseModel';
import * as AT from '../../components/container/attributes';
import {HitOnlineEnums} from 'hit-online-web-ui/src/helpers/constants';
import {translate} from '../../plugins/i18n/i18n';

export default class ItemData extends BaseModel {
  constructor({
    id,
    item,
    parent = undefined,
    resource = undefined,
    number = undefined,
    designation = undefined,
    unit = undefined,
    customisable = undefined,
    quantity = undefined,
    quantityExpression = undefined,
    validExpression = undefined,
    netPrice = undefined,
    netAmount = undefined,
    margin = undefined,
    grossPrice = undefined,
    grossAmount = undefined,
    quantityType = undefined,
    priceModified = undefined,
    marginModified = undefined,
    comment = undefined,
    designation_template = undefined,
    scriptParam = undefined,
    no = undefined,
    active = undefined,
    sortOrder = undefined,
  }) {
    super({
      id,
      number: number || no,
      designation,
    });
    this.item = item;
    this.parent = parent;
    this.resource = resource;
    this.no = no;
    this.designation = designation;
    this.unit = unit;
    this.customisable = customisable;
    this.quantity = quantity;
    this.quantityExpression = quantityExpression;
    this.validExpression = validExpression;
    this.netPrice = netPrice;
    this.netAmount = netAmount;
    this.margin = margin;
    this.grossPrice = grossPrice;
    this.grossAmount = grossAmount;
    this.priceModified = priceModified;
    this.marginModified = marginModified;
    this.comment = comment;
    this.scriptParam = scriptParam;
    this.designation_template = designation_template;
    this.active = active;
    this.quantityType = quantityType
      ? HitOnlineEnums.getEnumValue(
          'HIT_QUANTITY_TYPE',
          quantityType,
          translate,
        )
      : null;
    this.sortOrder = sortOrder;
  }

  static get fkName() {
    return 'item_id';
  }
  static get apiRoute() {
    return 'project_part_item_data';
  }
  static get detailPageRoute() {
    return undefined;
  }
  static get listPageRoute() {
    return undefined;
  }
  get fkName() {
    return ItemData.fkName;
  }
  get apiRoute() {
    return ItemData.apiRoute;
  }
  get listPageRoute() {
    return ItemData.listPageRoute;
  }
  get detailPageRoute() {
    return ItemData.detailPageRoute;
  }

  static get entityListAttrs() {
    return this.getAttributes(
      [
        'resource',
        'number',
        'designation',
        'unit',
        'quantity',
        'quantityTotal',
        'netPrice',
        'netAmount',
        'margin',
        'grossPrice',
        'grossAmount',
        'comment',
      ],
      [
        'validExpression',
        'quantityExpression',
        'quantityType',
        'priceModified',
        'marginModified',
        'customisable',
        'item',
        'scriptParam',
        'designation_template',
        'active',
        'sortOrder',
      ],
    );
  }

  /**
   * We can only pass the attributes that are present for all the three tables
   */
  static get allAttributes() {
    return [
      new AT.FkPositionItemAttribute({
        name: 'item',
        column: 'item_id',
        joinPath: [
          'project_part_item!fk_project_part_item_data_item_id_project_part_item',
        ],
        fkToUpdate: 'item_id',
        tableProperties: {
          header: 'hit-base.common.resource',
        },
      }),
      new AT.FkItemDataAttribute({
        name: 'parent',
        column: 'parent_id',
        joinPath: [
          'project_part_item_data!fk_project_part_item_data_parent_id_project_part_item_data',
        ],
        fkToUpdate: 'parent_id',
        tableProperties: {
          header: 'hit-base.common.resource',
        },
      }),
      new AT.FkResourceAttribute({
        name: 'resource',
        column: 'resource_id',
        default: '',
        joinPath: ['resource!fk_project_part_item_data_resource_id_resource'],
        fkToUpdate: 'resource_id',
        tableProperties: {
          header: 'ONLY_SHOW_IN_EDITION',
          onlyShowInEdition: true,
          hideInEditionUnlessHasResource: true,
        },
      }),
      this.NUMBER_ATTRIBUTE({}, {maxWidth: '0.75fr'}),
      this.DESIGNATION_ATTRIBUTE({}, {maxWidth: '1.5fr'}),
      new AT.FkUnitAttribute({
        name: 'unit',
        joinPath: ['unit!fk_project_part_item_data_unit_id_unit'],
        fkToUpdate: 'unit_id',
        tableProperties: {
          header: 'hit-base.common.unit',
          maxWidth: '0.5fr',
        },
      }),
      new AT.DecimalAttribute({
        name: 'quantity',
        decimalPrecision: 3,
        creationValue: 0,
        tableProperties: {
          header: 'hit-app.common.quantity',
          maxWidth: '0.60fr',
        },
      }),
      new AT.StringAttribute({
        name: 'quantityExpression',
        column: 'quantity_expression',
        tableProperties: {
          header: 'hit-app.item-data.expression',
        },
      }),
      new AT.DecimalAttribute({
        name: 'quantityTotal',
        column: 'total_quantity',
        decimalPrecision: 3,
        creationValue: 0,
        tableProperties: {
          header: 'hit-app.common.quantity-total',
          maxWidth: '0.75fr',
        },
      }),
      new AT.EnumerationAttribute({
        name: 'quantityType',
        enumType: 'QUANTITY_TYPE',
        enumClass: HitOnlineEnums,
        column: 'quantity_type',
      }),
      new AT.BooleanAttribute({
        name: 'validExpression',
        column: 'valid_expression',
      }),
      new AT.DecimalAttribute({
        name: 'netPrice',
        column: 'net_price',
        decimalPrecision: 2,
        creationValue: 0.0,
        tableProperties: {
          header: 'hit-app.common.net-price-short',
          maxWidth: '0.45fr',
          readOnly: true,
        },
      }),
      new AT.DecimalAttribute({
        name: 'netAmount',
        decimalPrecision: 2,
        column: 'net_amount',
        default: 0.0,
        tableProperties: {
          header: 'hit-app.common.net-amount-short',
          maxWidth: '0.65fr',
          readOnly: true,
        },
      }),
      new AT.DecimalAttribute({
        name: 'margin',
        decimalPrecision: 4,
        creationValue: 1,
        tableProperties: {
          header: 'hit-app.common.margin',
          maxWidth: '0.50fr',
        },
      }),
      new AT.DecimalAttribute({
        name: 'grossPrice',
        decimalPrecision: 2,
        column: 'gross_price',
        default: 0.0,
        tableProperties: {
          header: 'hit-app.common.gross-price-short',
          maxWidth: '0.55fr',
          readOnly: true,
        },
      }),
      new AT.DecimalAttribute({
        name: 'grossAmount',
        decimalPrecision: 2,
        column: 'gross_amount',
        default: 0.0,
        tableProperties: {
          header: 'hit-app.common.gross-amount-short',
          maxWidth: '0.65fr',
          readOnly: true,
        },
      }),
      new AT.BooleanAttribute({
        name: 'priceModified',
        column: 'price_modified',
      }),
      new AT.BooleanAttribute({
        name: 'marginModified',
        column: 'margin_modified',
      }),
      new AT.BooleanAttribute({
        name: 'customisable',
        column: 'customisable',
        default: false,
        tableProperties: {
          header: 'hit-app.common.customisable',
        },
      }),
      new AT.StringAttribute({
        name: 'comment',
        tableProperties: {
          header: 'hit-base.common.comment',
          maxWidth: '1fr',
        },
      }),
      new AT.JsonObjectAttribute({
        name: 'scriptParam',
        column: 'script_param',
      }),
      new AT.StringAttribute({
        name: 'designation_template',
        default: null,
      }),
      this.ACTIVE_ATTRIBUTE(),
      new AT.NumberAttribute({
        name: 'sortOrder',
        column: 'sort_order',
        readOnly: true,
      }),
    ];
  }
}
